import { posts, postRoutes, getPost } from "./data/posts";
import { authorProfileUrl, isSameAuthor, isTeamAuthor } from "./data/authors";
import lastmodDates from "./data/lastmod.json";
import { dictionaries } from "./i18n/dictionary";
import {
  caseRoutes,
  casesByRubro,
  getCase,
  localizeCase,
  publishedCases,
  RUBROS,
} from "./data/caseStudies";
import {
  solutionRoutes,
  getSolution,
  localizeSolution,
  publishedSolutions,
} from "./data/solutions";
import {
  industryRoutes,
  getIndustry,
  localizeIndustry,
  publishedIndustries,
} from "./data/industries";
import {
  LOCALES,
  ROUTES,
  POST_PREFIX,
  CASE_PREFIX,
  SOLUTION_PREFIX,
  INDUSTRY_PREFIX,
  DEFAULT_LOCALE,
  HTML_LANG,
  OG_LOCALE,
  localeFromPath,
  translatePath,
  type Locale,
} from "./i18n/config";

// Configuración de SEO compartida entre:
//  - el render del cliente (App.tsx, vía useEffect, para la navegación SPA)
//  - el prerender en build time (scripts/prerender.mjs, vía renderHeadTags)
// Tener una sola fuente de verdad evita que se desincronicen titles, descriptions,
// canonical, Open Graph y Twitter Card.

// Dominio primario: antes somos.waia.ar. Auditoria SEO (2026-07-20) detecto
// que cada canonical/sitemap/hreflang apuntaba a un dominio distinto del que
// realmente sirve el contenido en waia.ar, repartiendo autoridad entre dos
// superficies -esto no alcanza solo con el codigo, tambien necesita que el
// hosting/DNS realmente sirvan esta app en waia.ar-.
export const SITE_URL = "https://waia.ar";
export const SITE_NAME = "WAIA Technologies";
/** Marca corta para el final del title: 7 caracteres con el separador, contra
 *  los 21 de " - WAIA Technologies", que empujaban los titles mas alla de los
 *  ~60 que Google muestra. */
const TITLE_BRAND = " | WAIA";
export const OG_IMAGE = `${SITE_URL}/og-image.png`;
/** Medidas reales del archivo. Las redes las usan para reservar el espacio de
 *  la preview sin tener que descargar la imagen primero. */
export const OG_IMAGE_WIDTH = 1200;
export const OG_IMAGE_HEIGHT = 630;
export const SITE_LOCALE = "es_AR";

/** id del <script> de JSON-LD, compartido entre el prerender y el cliente. */
export const JSONLD_SCRIPT_ID = "waia-jsonld";

// 60 caracteres justos. Antes "WAIA Technologies | Empresa de Software e IA en
// Rosario, Argentina" (66): se cortaba, y no nombraba lo que la home ahora
// promete (empleados virtuales). "Empresa de software ... Argentina" sigue en la
// meta description, donde hay lugar para el descriptor completo.
export const defaultTitle = "WAIA: software a medida, IA y empleados virtuales en Rosario";
export const defaultMetaDescription =
  "WAIA es una empresa de software en Rosario, Argentina, especializada en inteligencia artificial: adopción de IA, e-commerce y desarrollo a medida.";

// Datos de la empresa. Solo hechos verificables desde el propio proyecto
// (redes del Footer, WhatsApp de FloatingButtons, dominio .ar, prensa
// verificada en /nosotros). No se inventan horarios ni mail de ventas: un dato
// falso en Schema.org es peor que un dato ausente, porque los motores lo citan
// como si fuera cierto. addressLocality/addressRegion sin streetAddress: el
// usuario prefiere no publicar la direccion exacta, pero la ciudad es un dato
// ya publico (prensa) y es justo lo que hace falta para el posicionamiento
// local ("empresas rosarinas de IA").
export const ORGANIZATION = {
  name: "WAIA Technologies",
  legalName: "WAIA SRL",
  phone: "+5493413070683",
  country: "AR",
  addressLocality: "Rosario",
  addressRegion: "Santa Fe",
  foundingDate: "2021",
  founderName: "Harald Valdivieso",
  numberOfEmployees: 25,
  sameAs: [
    "https://www.linkedin.com/company/72595080/",
    "https://www.instagram.com/somoswaia/",
    "https://www.youtube.com/@somoswaia",
    // Ficha de WAIA en el directorio de partners de OpenCart: es el respaldo de
    // tercero de "partner oficial de OpenCart en Argentina" (verificado el
    // 2026-11-03: "Waia Argentina | OpenCart Partner since 08/2021").
    "https://www.opencart.com/index.php?route=support/partner/info&partner_id=987639",
  ],
} as const;

// Lo que el nodo Organization dice de WAIA, por idioma. Un motor generativo cita
// este nodo como un hecho, asi que solo repite lo que el sitio muestra (por
// ejemplo, la respuesta de la home a "¿Desde cuándo trabaja WAIA con
// inteligencia artificial?"): ni servicios ni temas que la pagina no sostenga.
// Antes salia siempre en español, tambien en /en/..., y la oferta de IA hablaba
// de "modelos predictivos", que ninguna pagina menciona.
const ORG_COPY: Record<
  Locale,
  {
    description: string;
    areaServed: string[];
    knowsAbout: string[];
    offers: { name: string; description: string }[];
  }
> = {
  es: {
    description: defaultMetaDescription,
    areaServed: ["Rosario, Argentina", "Argentina", "Latinoamérica"],
    knowsAbout: [
      "Desarrollo de software a medida",
      "E-commerce",
      "OpenCart",
      "Inteligencia artificial aplicada a negocios",
      "Agentes conversacionales",
      "Automatización de procesos",
      "Integración de medios de pago y envío",
    ],
    offers: [
      {
        name: "Desarrollo de software a medida",
        description:
          "Sistemas y aplicaciones desarrolladas a medida para resolver necesidades específicas de cada empresa.",
      },
      {
        name: "E-commerce",
        description:
          "Desarrollo y optimización de tiendas online, con integración de medios de pago y envío.",
      },
      {
        name: "Inteligencia artificial",
        description:
          "Agentes conversacionales, automatizaciones y audio narrado con IA, además de IA integrada en proyectos de software a medida y e-commerce.",
      },
    ],
  },
  en: {
    description:
      "WAIA is a software company in Rosario, Argentina, specialized in artificial intelligence: AI adoption, e-commerce and custom software development.",
    areaServed: ["Rosario, Argentina", "Argentina", "Latin America"],
    knowsAbout: [
      "Custom software development",
      "E-commerce",
      "OpenCart",
      "Artificial intelligence applied to business",
      "Conversational agents",
      "Process automation",
      "Payment and shipping integration",
    ],
    offers: [
      {
        name: "Custom software development",
        description:
          "Systems and applications built to solve the specific needs of each company.",
      },
      {
        name: "E-commerce",
        description:
          "Development and optimization of online stores, with payment and shipping integration.",
      },
      {
        name: "Artificial intelligence",
        description:
          "Conversational agents, automations and AI-narrated audio, plus AI integrated into custom software and e-commerce projects.",
      },
    ],
  },
};

export interface SeoEntry {
  title: string;
  description: string;
  /**
   * Nombre corto de la pagina para el breadcrumb. Ausente en la home.
   */
  breadcrumb?: string;
}

export const seoByPath: Record<string, SeoEntry> = {
  "/": {
    title: defaultTitle,
    // 207 caracteres originales cortaban el CTA en el snippet de Google
    // (limite util ~160). Se recorta manteniendo la keyword de
    // posicionamiento intacta al principio, que es lo que Google negrita.
    description:
      "Empresa de software en Rosario, Argentina especializada en IA: desarrollo a medida, e-commerce e IA aplicada a negocios reales. +500 proyectos entregados →",
  },
  "/nosotros": {
    title: "Sobre WAIA | Empresa de Software e IA en Rosario, Argentina",
    description:
      "Conocé al equipo detrás de nuestra empresa de software e inteligencia artificial en Rosario: la historia, los valores y la prensa que respalda a WAIA. Sumate →",
    breadcrumb: "Nosotros",
  },
  "/empresas": {
    title: "Software e IA a medida para empresas | WAIA Rosario",
    description:
      "Software a medida, IA y equipos técnicos para empresas: diagnóstico, entregas verificables y acompañamiento tras el lanzamiento. Agendá un diagnóstico →",
    breadcrumb: "Para empresas",
  },
  "/casos": {
    title: "Casos de Éxito | Proyectos reales de WAIA",
    description:
      "Todos los casos de éxito de WAIA: e-commerce, software a medida e inteligencia artificial, con resultados medibles. Mirá los proyectos →",
    breadcrumb: "Casos de Éxito",
  },
  "/soluciones": {
    // El title anterior (34 caracteres, "Soluciones | Qué resolvemos") no
    // llevaba ninguna keyword de posicionamiento -desperdiciaba el tag mas
    // pesado para SEO de la pagina.
    title: "Soluciones de Software e IA en Rosario, Argentina - WAIA",
    description:
      "Lo que resuelve nuestra empresa de software en Rosario, Argentina: agentes de IA, automatización, software a medida, e-commerce B2B y cloud →",
    breadcrumb: "Soluciones",
  },
  "/industrias": {
    title: "Industrias | Software e IA por rubro - WAIA",
    description:
      "Software e IA aplicados a salud, sector público, retail, e-commerce, hotelería, industria y banca y seguros, con el caso real detrás de cada rubro →",
    breadcrumb: "Industrias",
  },
  "/casos/ecommerce": {
    title: "Casos de E-commerce | Tiendas online que venden - WAIA",
    description:
      "Proyectos reales de tiendas online con resultados medibles: +50% de optimización de ganancias en ventas y +30% de ahorro en pasarelas de pago. Mirá los casos →",
    breadcrumb: "Casos de E-commerce",
  },
  "/casos/software-factory": {
    title: "Casos de Software Factory | Desarrollo a medida - WAIA",
    description:
      "Desarrollo a medida con calidad técnica comprobada, de Marriott a Grupo Emerger. Sistemas robustos para desafíos complejos →",
    breadcrumb: "Casos de Software Factory",
  },
  "/novedades": {
    title: "Novedades | Lo que aprendimos construyendo - WAIA",
    description:
      "Notas sobre e-commerce, software a medida e inteligencia artificial, escritas desde los proyectos que hacemos. Leé las últimas →",
    breadcrumb: "Novedades",
  },
  "/casos/inteligencia-artificial": {
    title: "Casos de Inteligencia Artificial aplicada a negocios - WAIA",
    description:
      "IA en negocios reales: un agente conversacional por WhatsApp, audio narrado con la voz de los periodistas y guías interactivas. Descubrí los casos →",
    breadcrumb: "Casos de Inteligencia Artificial",
  },

  // --- Inglés ---
  "/en": {
    title: "WAIA: custom software, AI and virtual employees in Rosario",
    description:
      "Software company in Rosario, Argentina specialized in AI: custom development, e-commerce and applied AI for real businesses. 500+ projects delivered →",
  },
  "/en/about": {
    title: "About WAIA | Software & AI Company in Rosario, Argentina",
    description:
      "Meet the team behind our software and AI company in Rosario: the history, the values and the press coverage backing WAIA. Join us →",
    breadcrumb: "About",
  },
  "/en/enterprises": {
    title: "Custom Software & AI for Enterprises | WAIA Rosario",
    description:
      "Custom software, AI and technical teams for companies: diagnosis first, verifiable deliveries and support after launch. Book a diagnosis →",
    breadcrumb: "For enterprises",
  },
  "/en/cases": {
    title: "Success Stories | Real WAIA projects",
    description:
      "All of WAIA's success stories: e-commerce, custom software and artificial intelligence, with measurable results. See the projects →",
    breadcrumb: "Success Stories",
  },
  "/en/solutions": {
    title: "Software and AI Solutions in Rosario, Argentina - WAIA",
    description:
      "What our software company in Rosario, Argentina solves: AI agents, automation, custom software, B2B e-commerce and cloud infrastructure →",
    breadcrumb: "Solutions",
  },
  "/en/industries": {
    title: "Industries | Software and AI by sector - WAIA",
    description:
      "Software and AI applied to healthcare, public sector, retail, e-commerce, hospitality, industry and banking and insurance, with the real case behind each →",
    breadcrumb: "Industries",
  },
  "/en/cases/ecommerce": {
    title: "E-commerce case studies | Online stores that sell - WAIA",
    description:
      "Real online store projects with measurable results: +50% sales profitability and +30% savings on payment gateway fees. See the case studies →",
    breadcrumb: "E-commerce cases",
  },
  "/en/cases/software-factory": {
    title: "Software Factory case studies | Custom development - WAIA",
    description:
      "Custom development with proven technical quality, from Marriott to Grupo Emerger. Robust systems for complex challenges →",
    breadcrumb: "Software Factory cases",
  },
  "/en/cases/artificial-intelligence": {
    title: "Artificial Intelligence case studies for business - WAIA",
    description:
      "AI in real businesses: a WhatsApp conversational agent, audio narrated in the journalists' own voices and interactive guides. Explore the case studies →",
    breadcrumb: "AI cases",
  },
  "/en/news": {
    title: "Insights | What we learned building - WAIA",
    description:
      "Notes on e-commerce, custom software and artificial intelligence, written from the projects we deliver. Read the latest →",
    breadcrumb: "Insights",
  },
};

// Rutas que se pre-renderizan a HTML estático en el build: las fijas de arriba
// mas una por cada nota publicada. Al agregar un .md aparece sola.
export const prerenderRoutes = [
  ...Object.keys(seoByPath),
  ...postRoutes,
  ...caseRoutes,
  ...solutionRoutes,
  ...industryRoutes,
];

/**
 * Rutas que existen -necesitan su propio HTML estatico para que Nginx las
 * sirva- pero que NO son contenido publico: herramientas internas. Se
 * pre-renderizan aparte (scripts/prerender.mjs las suma a mano) y quedan
 * afuera de prerenderRoutes a proposito, que es de donde sale el sitemap
 * (renderSitemap) y el listado de rutas "reales" del sitio.
 */
export const NOINDEX_ROUTES = ["/panel"];

/**
 * Prefijos que tambien van noindex pero no se pueden listar como rutas fijas
 * en NOINDEX_ROUTES: el slug de cada propuesta lo crea el panel DESPUES del
 * build (ver /propuestas/:slug en App.tsx), asi que nunca se puede conocer
 * de antemano.
 */
const NOINDEX_PREFIXES = ["/propuestas/", "/contratos/"];

export function isNoindexPath(pathname: string): boolean {
  return (
    NOINDEX_ROUTES.includes(pathname) ||
    // El HTML de la pagina 404 (la sirve nginx con status 404): no es contenido.
    pathname === "/404" ||
    NOINDEX_PREFIXES.some((prefix) => pathname.startsWith(prefix))
  );
}

/**
 * Slug de la nota si la ruta es una nota, en cualquier idioma. Estaba fijo en
 * "/novedades/", con lo que las notas en ingles (/en/news/...) se quedaban sin
 * title, description ni datos estructurados propios.
 */
function postSlugFromPath(pathname: string): string | null {
  const prefix = POST_PREFIX[localeFromPath(pathname)];
  return pathname.startsWith(`${prefix}/`)
    ? pathname.slice(prefix.length + 1)
    : null;
}

/** Slug del caso si la ruta es un caso, en cualquier idioma. */
function caseSlugFromPath(pathname: string): string | null {
  const prefix = CASE_PREFIX[localeFromPath(pathname)];
  return pathname.startsWith(`${prefix}/`)
    ? pathname.slice(prefix.length + 1)
    : null;
}

/** Slug de la solucion si la ruta es una landing de solucion, en cualquier idioma. */
function solutionSlugFromPath(pathname: string): string | null {
  const prefix = SOLUTION_PREFIX[localeFromPath(pathname)];
  return pathname.startsWith(`${prefix}/`)
    ? pathname.slice(prefix.length + 1)
    : null;
}

/** Slug de la industria si la ruta es una landing de industria, en cualquier idioma. */
function industrySlugFromPath(pathname: string): string | null {
  const prefix = INDUSTRY_PREFIX[localeFromPath(pathname)];
  return pathname.startsWith(`${prefix}/`)
    ? pathname.slice(prefix.length + 1)
    : null;
}

/**
 * Traduce una ruta al otro idioma, sabiendo que las notas cambian de slug
 * (a diferencia de los casos, que conservan el mismo). translatePath() a
 * secas asume el mismo slug en los dos idiomas -correcto para casos- y
 * armaria una URL que no existe para una nota. La usan el selector de idioma
 * y el hreflang, que necesitan la URL real de la traduccion.
 */
export function translateLocalizedPath(pathname: string, target: Locale): string {
  if (localeFromPath(pathname) === target) return pathname;

  const postSlug = postSlugFromPath(pathname);

  if (postSlug) {
    const post = getPost(postSlug);
    const targetSlug = post?.translationSlug ?? postSlug;
    return `${POST_PREFIX[target]}/${targetSlug}`;
  }

  return translatePath(pathname, target);
}

/** Largo maximo de una meta description antes de que Google la corte en los
 * resultados (~160 caracteres). */
const MAX_DESCRIPTION = 158;

/** Piso para cortar en una clausula: mas corto que esto la description queda pobre. */
const MIN_CLAUSE_DESCRIPTION = 100;
/** Piso para quedarse con una oracion corta antes que con un corte con "…". */
const MIN_SENTENCE_DESCRIPTION = 70;
/** Palabras que no pueden quedar al final de un corte con "…" (es/en). */
const DANGLING_WORDS =
  /(?:\s+(?:el|la|los|las|un|una|de|del|al|en|y|e|o|u|que|con|por|para|sin|su|sus|the|an|of|to|for|and|or|in|on|at|with|by|from|that|your|our))+$/i;

/**
 * Ajusta un resumen al largo de una meta description. Los resumenes de notas,
 * soluciones, industrias y casos se escriben para la tarjeta y la pagina -hay
 * notas de 400 caracteres-, y como description pegaban un corte arbitrario de
 * Google a mitad de frase. Orden de preferencia, siempre sin cortar una frase
 * a la mitad: 1) una oracion completa, 2) una clausula completa (hasta una
 * coma, punto y coma o dos puntos, cerrada con punto), 3) una oracion corta,
 * 4) como ultimo recurso, el limite de una palabra con puntos suspensivos. Los
 * textos que ya entran quedan intactos. Una pagina que necesite una description
 * mejor que cualquiera de estas la declara aparte (`metaDescription`).
 */
export function fitDescription(text: string): string {
  const clean = text.replace(/\s+/g, " ").trim();
  if (clean.length <= MAX_DESCRIPTION) return clean;

  const head = clean.slice(0, MAX_DESCRIPTION);
  const sentenceEnd = Math.max(head.lastIndexOf(". "), head.lastIndexOf("? "), head.lastIndexOf("! "));
  if (sentenceEnd >= MAX_DESCRIPTION * 0.55) return head.slice(0, sentenceEnd + 1);

  // Un resumen de una sola oracion larga no tiene punto: se cierra en la ultima
  // clausula completa. Se descartan los cortes que dejan un parentesis, unas
  // comillas o un inciso entre guiones abiertos. No se corta en dos puntos: lo
  // que anuncian es justo lo que quedaria afuera ("...que una plantilla:").
  for (let i = head.length - 1; i >= MIN_CLAUSE_DESCRIPTION; i--) {
    if (/[,;]/.test(head[i]) && clean[i + 1] === " " && isBalanced(head.slice(0, i))) {
      const clause = head.slice(0, i).replace(/[\s\-–—(]+$/, "");
      return clause.endsWith(".") ? clause : `${clause}.`;
    }
  }

  if (sentenceEnd >= MIN_SENTENCE_DESCRIPTION) return head.slice(0, sentenceEnd + 1);

  const room = clean.slice(0, MAX_DESCRIPTION - 1);
  const cut = room.slice(0, room.lastIndexOf(" ")).replace(/[\s,;:\-–—(]+$/, "");
  return `${cut.replace(DANGLING_WORDS, "")}…`;
}

/**
 * `metaDescription` declarada para el idioma de la ruta. localizeSolution y
 * localizeIndustry mezclan `en` sobre el español: sin leerla asi, una pagina
 * en ingles sin la suya mostraria la del español.
 */
function ownMetaDescription(
  item: { metaDescription?: string; en?: { metaDescription?: string } },
  locale: Locale,
): string | undefined {
  return locale === "en" ? item.en?.metaDescription : item.metaDescription;
}

/** Parentesis, comillas tipograficas e incisos entre guiones (" -asi- ") cerrados. */
function isBalanced(text: string): boolean {
  const count = (re: RegExp) => (text.match(re) ?? []).length;
  return (
    count(/\(/g) === count(/\)/g) &&
    count(/“/g) === count(/”/g) &&
    count(/(?<=^|\s)-(?=\S)/g) === count(/(?<=\S)-(?=\s|$)/g)
  );
}

export function getSeo(pathname: string): SeoEntry {
  // Herramienta interna: title propio pero fuera de seoByPath a proposito
  // -ese objeto alimenta prerenderRoutes (de ahi sale el sitemap), y esto no
  // es contenido publico que deba aparecer ahi (ver NOINDEX_ROUTES).
  if (pathname === "/panel") {
    return {
      title: `Panel - ${SITE_NAME}`,
      description: "Panel interno de gestión de contenido.",
    };
  }

  // Propuestas comerciales: mismo criterio que /panel, title propio fuera de
  // seoByPath -el slug real (y su cliente) no existen todavia al pre-renderizar
  // el shell generico, ver NOINDEX_PREFIXES arriba-.
  if (pathname.startsWith("/propuestas/")) {
    return {
      title: `Propuesta comercial - ${SITE_NAME}`,
      description: "Propuesta comercial privada de WAIA Technologies.",
    };
  }

  // Contratos para aprobar: lo mismo que las propuestas (slug creado en el panel
  // despues del build, shell generico pre-renderizado, siempre noindex).
  if (pathname.startsWith("/contratos/")) {
    return {
      title: `Contrato - ${SITE_NAME}`,
      description: "Contrato privado de WAIA Technologies.",
    };
  }

  // La pagina 404 tenia el title y la description de la home: duplicaba sus
  // metadatos. Title propio, fuera de seoByPath (no es una ruta publica).
  if (pathname === "/404") {
    return {
      title: `${dictionaries.es.notFound.title}${TITLE_BRAND}`,
      description: "La página que buscás no existe o cambió de lugar. Volvé al inicio de WAIA.",
    };
  }

  // Los casos no estan en seoByPath: su SEO sale del propio caso.
  const caseSlug = caseSlugFromPath(pathname);

  if (caseSlug) {
    const rawStudy = getCase(caseSlug);
    if (rawStudy) {
      const study = localizeCase(rawStudy, localeFromPath(pathname));
      return {
        title: `${study.seoTitle ?? `${study.client}: ${study.title}`}${TITLE_BRAND}`,
        description: fitDescription(study.summary),
        breadcrumb: study.client,
      };
    }
  }

  // Las soluciones no estan en seoByPath: su SEO sale de la propia solucion.
  const solutionSlug = solutionSlugFromPath(pathname);

  if (solutionSlug) {
    const rawSolution = getSolution(solutionSlug);
    if (rawSolution) {
      const solutionLocale = localeFromPath(pathname);
      const solution = localizeSolution(rawSolution, solutionLocale);
      return {
        title: `${solution.seoTitle ?? solution.title}${TITLE_BRAND}`,
        description: ownMetaDescription(rawSolution, solutionLocale) ?? fitDescription(solution.summary),
        breadcrumb: solution.title,
      };
    }
  }

  // Las industrias no estan en seoByPath: su SEO sale de la propia industria.
  const industrySlug = industrySlugFromPath(pathname);

  if (industrySlug) {
    const rawIndustry = getIndustry(industrySlug);
    if (rawIndustry) {
      const industryLocale = localeFromPath(pathname);
      const industry = localizeIndustry(rawIndustry, industryLocale);
      return {
        title: `${industry.seoTitle ?? industry.title}${TITLE_BRAND}`,
        description: ownMetaDescription(rawIndustry, industryLocale) ?? fitDescription(industry.summary),
        breadcrumb: industry.title,
      };
    }
  }

  // Las notas no estan en seoByPath: su SEO sale del frontmatter del .md.
  const postSlug = postSlugFromPath(pathname);

  if (postSlug) {
    const post = getPost(postSlug);
    if (post) {
      return {
        // seoTitle (frontmatter) es la version corta del titular para el
        // buscador; el titular completo sigue siendo el H1 de la nota. Si no
        // esta, se usa el titular tal cual.
        title: `${post.seoTitle ?? post.title}${TITLE_BRAND}`,
        description: fitDescription(post.summary),
        breadcrumb: post.title,
      };
    }
  }

  const seo = seoByPath[pathname];
  return {
    title: seo?.title ?? defaultTitle,
    description: seo?.description ?? defaultMetaDescription,
    // Se propaga tal cual (undefined en la home y en el 404): sin esto el
    // BreadcrumbList no se emitia nunca, porque este objeto se reconstruye
    // campo por campo y el breadcrumb se perdia en el camino.
    breadcrumb: seo?.breadcrumb,
  };
}

/**
 * Tipo Schema.org de la pagina: "Sobre nosotros" y los indices que listan otras
 * paginas tienen su tipo propio, el resto es una WebPage generica. Le dice a un
 * motor generativo que lo que lee es un perfil de la empresa o un catalogo, no
 * un articulo suelto.
 */
function pageTypeFor(pathname: string): "WebPage" | "AboutPage" | "CollectionPage" {
  const is = (route: { es: string; en: string }) =>
    pathname === route.es || pathname === route.en;

  if (is(ROUTES.about)) return "AboutPage";

  const collections = [
    ROUTES.cases,
    ROUTES.solutions,
    ROUTES.industries,
    ROUTES.news,
    ...Object.values(RUBROS).map((rubro) => ROUTES[rubro.routeKey]),
  ];

  return collections.some(is) ? "CollectionPage" : "WebPage";
}

/**
 * Las paginas que lista un indice, con su nombre y ruta. Undefined si la ruta no
 * es un indice (o es el de las notas, que crece sin limite y ya tiene su propio
 * listado visible).
 */
function collectionItems(pathname: string): { name: string; path: string }[] | undefined {
  const locale = localeFromPath(pathname);
  const is = (route: { es: string; en: string }) =>
    pathname === route.es || pathname === route.en;

  const caseItems = (list: typeof publishedCases) =>
    list.map((c) => {
      const study = localizeCase(c, locale);
      return { name: `${study.client}: ${study.title}`, path: `${CASE_PREFIX[locale]}/${c.slug}` };
    });

  if (is(ROUTES.cases)) return caseItems(publishedCases);

  const rubro = Object.entries(RUBROS).find(([, r]) => is(ROUTES[r.routeKey]));
  if (rubro) return caseItems(casesByRubro(rubro[0] as keyof typeof RUBROS));

  if (is(ROUTES.solutions)) {
    return publishedSolutions.map((s) => ({
      name: localizeSolution(s, locale).title,
      path: `${SOLUTION_PREFIX[locale]}/${s.slug}`,
    }));
  }

  if (is(ROUTES.industries)) {
    return publishedIndustries.map((i) => ({
      name: localizeIndustry(i, locale).title,
      path: `${INDUSTRY_PREFIX[locale]}/${i.slug}`,
    }));
  }

  return undefined;
}

/**
 * Datos estructurados Schema.org de la pagina.
 *
 * Es la pieza central para que un motor generativo (ChatGPT, Perplexity,
 * Google AI Overviews) entienda QUE entidad es WAIA y no tenga que deducirlo
 * del texto: le da nombre, rubro, servicios y perfiles oficiales de forma
 * explicita y sin ambiguedad.
 *
 * Se emite como un @graph unico en vez de varios <script> sueltos, asi los
 * nodos se referencian entre si por @id.
 */
export function buildJsonLd(pathname: string): unknown {
  const seo = getSeo(pathname);
  const url = `${SITE_URL}${pathname}`;
  const orgId = `${SITE_URL}/#organization`;
  const siteId = `${SITE_URL}/#website`;
  const founderId = `${SITE_URL}/#founder`;
  const founderProfile = authorProfileUrl(ORGANIZATION.founderName);
  const org = ORG_COPY[localeFromPath(pathname)];
  const modified = lastmodFor(pathname);

  const webPage: Record<string, unknown> = {
    "@type": pageTypeFor(pathname),
    "@id": `${url}#webpage`,
    url,
    name: seo.title,
    description: seo.description,
    inLanguage: HTML_LANG[localeFromPath(pathname)],
    isPartOf: { "@id": siteId },
    about: { "@id": orgId },
    // Misma fecha que el <lastmod> del sitemap (sale del ultimo commit que toco
    // la pagina): una fecha real que un motor generativo puede usar para decidir
    // si el contenido esta vigente. Sin fecha confiable se omite.
    ...(modified ? { dateModified: modified } : {}),
    ...(seo.breadcrumb ? { breadcrumb: { "@id": `${url}#breadcrumb` } } : {}),
  };

  const graph: Record<string, unknown>[] = [
    {
      // Los dos tipos en el mismo nodo (mismo @id): WAIA es una Organization
      // (razon social, servicios) que ademas opera desde una oficina fisica
      // en Rosario -LocalBusiness es lo que habilita el pack local de Google
      // y las citas de "software factory en Rosario" con ubicacion real.
      // Sin direccion de calle a proposito: el usuario prefiere no publicarla,
      // la ciudad/provincia ya alcanzan para las señales de SEO local.
      "@type": ["Organization", "LocalBusiness"],
      "@id": orgId,
      name: ORGANIZATION.name,
      // "WAIA" a secas es como lo nombran el sitio, la prensa y los clientes:
      // sin este alias un motor puede tratarlo como una entidad distinta.
      alternateName: "WAIA",
      legalName: ORGANIZATION.legalName,
      url: SITE_URL,
      logo: `${SITE_URL}/og-image.png`,
      image: OG_IMAGE,
      description: org.description,
      telephone: ORGANIZATION.phone,
      foundingDate: ORGANIZATION.foundingDate,
      foundingLocation: {
        "@type": "Place",
        name: `${ORGANIZATION.addressLocality}, ${ORGANIZATION.addressRegion}, Argentina`,
      },
      founder: { "@id": founderId },
      numberOfEmployees: {
        "@type": "QuantitativeValue",
        value: ORGANIZATION.numberOfEmployees,
      },
      address: {
        "@type": "PostalAddress",
        addressLocality: ORGANIZATION.addressLocality,
        addressRegion: ORGANIZATION.addressRegion,
        addressCountry: ORGANIZATION.country,
      },
      // En el idioma de la pagina: los motores generativos en ingles citan la
      // version en ingles del dato, no traducen "Rosario, Argentina" solos.
      areaServed: org.areaServed,
      sameAs: [...ORGANIZATION.sameAs],
      knowsAbout: org.knowsAbout,
      makesOffer: org.offers.map((offer) => ({
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: offer.name,
          description: offer.description,
        },
      })),
    },
    {
      // El fundador es una entidad propia (mismo @id en el Organization y en
      // las notas que firma): sin ella, "Harald Valdivieso" es un string suelto
      // y los motores no lo pueden unir a la empresa ni a su perfil. Solo lleva
      // datos ya publicados; jobTitle sale del propio "founder" de la empresa.
      "@type": "Person",
      "@id": founderId,
      name: ORGANIZATION.founderName,
      jobTitle: "Founder",
      worksFor: { "@id": orgId },
      ...(founderProfile ? { url: founderProfile, sameAs: [founderProfile] } : {}),
    },
    {
      "@type": "WebSite",
      "@id": siteId,
      url: SITE_URL,
      name: SITE_NAME,
      inLanguage: HTML_LANG[localeFromPath(pathname)],
      publisher: { "@id": orgId },
    },
    webPage,
  ];

  // Los indices (casos, soluciones, industrias) listan sus paginas como un
  // ItemList: un motor generativo que pregunta "que casos tiene WAIA" lee la
  // enumeracion completa y con sus URLs, en vez de deducirla del HTML.
  const listItems = collectionItems(pathname);

  if (listItems) {
    graph.push({
      "@type": "ItemList",
      "@id": `${url}#list`,
      numberOfItems: listItems.length,
      itemListElement: listItems.map((item, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: item.name,
        url: `${SITE_URL}${item.path}`,
      })),
    });
    webPage.mainEntity = { "@id": `${url}#list` };
  }

  // Un caso es un Article sobre trabajo de WAIA: le da a los motores el cliente,
  // el rubro y el editor. about apunta al cliente para que quede claro de quien
  // habla, y publisher a WAIA como autor del proyecto.
  const caseSlug = caseSlugFromPath(pathname);
  const rawCaseStudy = caseSlug ? getCase(caseSlug) : undefined;

  if (rawCaseStudy) {
    const caseLocale = localeFromPath(pathname);
    const study = localizeCase(rawCaseStudy, caseLocale);
    const rubroLabel =
      caseLocale === "en" ? RUBROS[study.rubro].labelEn : RUBROS[study.rubro].label;

    graph.push({
      "@type": "Article",
      "@id": `${url}#article`,
      headline: `${study.client}: ${study.title}`,
      description: study.summary,
      inLanguage: HTML_LANG[caseLocale],
      articleSection: rubroLabel,
      author: { "@id": orgId },
      publisher: { "@id": orgId },
      isPartOf: { "@id": siteId },
      mainEntityOfPage: { "@id": `${url}#webpage` },
      about: { "@type": "Organization", name: study.client },
      // La portada del caso, no la imagen generica del sitio. Un asset chico que
      // el build incrusta como data: URI no sirve como URL de imagen: en ese
      // caso se queda con la generica.
      image: study.images?.[0]?.src.startsWith("/")
        ? `${SITE_URL}${study.images[0].src}`
        : OG_IMAGE,
      ...(modified ? { dateModified: modified } : {}),
      isAccessibleForFree: true,
    });
  }

  // Una landing de solucion es un Service concreto de WAIA -mas especifico
  // que los 3 makesOffer genericos del Organization de arriba-, para que un
  // motor generativo entienda exactamente que problema resuelve esta pagina.
  const solutionSlug = solutionSlugFromPath(pathname);
  const rawSolution = solutionSlug ? getSolution(solutionSlug) : undefined;

  if (rawSolution) {
    const solutionLocale = localeFromPath(pathname);
    const solution = localizeSolution(rawSolution, solutionLocale);

    graph.push({
      "@type": "Service",
      "@id": `${url}#service`,
      name: solution.title,
      description: solution.summary,
      ...(solution.serviceType ? { serviceType: solution.serviceType } : {}),
      ...(solution.audience
        ? { audience: { "@type": "BusinessAudience", audienceType: solution.audience } }
        : {}),
      inLanguage: HTML_LANG[solutionLocale],
      provider: { "@id": orgId },
      areaServed: ["Rosario, Argentina", "Argentina"],
      mainEntityOfPage: { "@id": `${url}#webpage` },
    });

    // Terceros que la pagina nombra: entidades aparte, enlazadas solo como
    // `mentions` de la pagina. Nunca como provider ni memberOf, que afirmarian
    // un vinculo formal que la pagina no declara.
    if (solution.mentions?.length) {
      const mentioned = solution.mentions.map((m) => ({
        "@type": "Organization",
        // En el espacio de WAIA y no en el de Certenza: un @id con su dominio se
        // fusionaria con su propio grafo si publican el mismo identificador.
        "@id": `${url}#${m.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`,
        name: m.name,
        url: m.url,
      }));
      graph.push(...mentioned);
      webPage.mentions = mentioned.map((m) => ({ "@id": m["@id"] }));
    }

    // FAQPage: alimenta People Also Ask y las respuestas de buscadores
    // generativos, ademas de habilitar el rich result de preguntas y
    // respuestas en el resultado de busqueda.
    if (solution.faq.length > 0) {
      graph.push({
        "@type": "FAQPage",
        "@id": `${url}#faq`,
        inLanguage: HTML_LANG[solutionLocale],
        mainEntity: solution.faq.map((item) => ({
          "@type": "Question",
          name: item.question,
          acceptedAnswer: {
            "@type": "Answer",
            text: item.answer,
          },
        })),
      });
    }

    // HowTo solo en las landings con vista propia (AutomationSolutionPage,
    // EcommerceB2BPage): son las unicas soluciones con un proceso propio en
    // pasos, no texto generico. Habilita el rich result de "pasos" en el
    // resultado de busqueda.
    const CUSTOM_LANDING_STEPS: Partial<Record<string, "automationLanding" | "ecommerceB2BLanding">> = {
      "automatizacion-procesos-ia": "automationLanding",
      "ecommerce-b2b-mayorista": "ecommerceB2BLanding",
    };
    const landingKey = solutionSlug ? CUSTOM_LANDING_STEPS[solutionSlug] : undefined;

    if (landingKey) {
      const steps = dictionaries[solutionLocale][landingKey].steps;
      graph.push({
        "@type": "HowTo",
        "@id": `${url}#howto`,
        name: solution.title,
        description: solution.summary,
        inLanguage: HTML_LANG[solutionLocale],
        step: steps.map((s, i) => ({
          "@type": "HowToStep",
          position: i + 1,
          name: s.title,
          text: s.description,
        })),
      });
    }
  }

  // Una landing de industria tambien es un Service, con serviceType marcando
  // el rubro: distingue "que resolvemos" (solucion) de "para quien" (industria)
  // sin duplicar el mismo tipo de dato de forma ambigua.
  const industrySlug = industrySlugFromPath(pathname);
  const rawIndustry = industrySlug ? getIndustry(industrySlug) : undefined;

  if (rawIndustry) {
    const industryLocale = localeFromPath(pathname);
    const industry = localizeIndustry(rawIndustry, industryLocale);

    graph.push({
      "@type": "Service",
      "@id": `${url}#service`,
      name: industry.title,
      description: industry.summary,
      serviceType: industry.shortLabel,
      inLanguage: HTML_LANG[industryLocale],
      provider: { "@id": orgId },
      areaServed: ["Rosario, Argentina", "Argentina"],
      mainEntityOfPage: { "@id": `${url}#webpage` },
    });

    // Las mismas preguntas que la pagina muestra en su acordeon (Google pide que
    // lo estructurado coincida con lo visible).
    if (industry.faq && industry.faq.length > 0) {
      graph.push({
        "@type": "FAQPage",
        "@id": `${url}#faq`,
        inLanguage: HTML_LANG[industryLocale],
        mainEntity: industry.faq.map((item) => ({
          "@type": "Question",
          name: item.question,
          acceptedAnswer: {
            "@type": "Answer",
            text: item.answer,
          },
        })),
      });
    }
  }

  // /empresas: FAQPage con las mismas preguntas que el acordeon visible (Google
  // pide que lo estructurado coincida con lo que se ve en la pagina).
  if (pathname === ROUTES.enterprise.es || pathname === ROUTES.enterprise.en) {
    const enterpriseLocale = localeFromPath(pathname);

    graph.push({
      "@type": "FAQPage",
      "@id": `${url}#faq`,
      inLanguage: HTML_LANG[enterpriseLocale],
      mainEntity: dictionaries[enterpriseLocale].enterprise.faq.items.map((item) => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: item.answer,
        },
      })),
    });
  }

  // La home tambien suma su propio FAQPage: preguntas generales sobre la
  // empresa (no atadas a una solucion puntual) que alimentan People Also Ask
  // y el rich result de preguntas y respuestas para "empresa de software en
  // Rosario/Argentina", la busqueda que mas queremos ganar.
  if (pathname === ROUTES.home.es || pathname === ROUTES.home.en) {
    const homeLocale = localeFromPath(pathname);
    const homeFaqItems = dictionaries[homeLocale].homeFaq.items;

    graph.push({
      "@type": "FAQPage",
      "@id": `${url}#faq`,
      inLanguage: HTML_LANG[homeLocale],
      mainEntity: homeFaqItems.map((item) => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: item.answer,
        },
      })),
    });

    // Los empleados virtuales como catalogo de servicios. Refleja lo que la
    // home ya muestra (slide del hero y seccion #empleados): mismos nombres y
    // bajadas que el HTML visible, no datos aparte -Google pide que lo
    // estructurado coincida con lo que se ve en la pagina-.
    const virtualTeam = dictionaries[homeLocale].virtualTeam;

    graph.push({
      "@type": "Service",
      "@id": `${url}#virtual-employees`,
      name: virtualTeam.eyebrow,
      description: virtualTeam.lead,
      inLanguage: HTML_LANG[homeLocale],
      provider: { "@id": orgId },
      areaServed: org.areaServed,
      mainEntityOfPage: { "@id": `${url}#webpage` },
      hasOfferCatalog: {
        "@type": "OfferCatalog",
        name: virtualTeam.eyebrow,
        itemListElement: virtualTeam.members.map((member) => ({
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: member.role,
            description: member.tagline,
          },
        })),
      },
    });
  }

  // El indice de /industrias no tenia ningun FAQPage: era la unica seccion
  // de peso (7 rubros) sin ese rich result, detectado en la auditoria de SEO.
  // Preguntas transversales (no por rubro especifico), asi que van una sola
  // vez en el indice y no repetidas en cada pagina de industria.
  if (pathname === ROUTES.industries.es || pathname === ROUTES.industries.en) {
    const industriesLocale = localeFromPath(pathname);
    const industriesFaqItems = dictionaries[industriesLocale].industries.faq;

    graph.push({
      "@type": "FAQPage",
      "@id": `${url}#faq`,
      inLanguage: HTML_LANG[industriesLocale],
      mainEntity: industriesFaqItems.map((item) => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: item.answer,
        },
      })),
    });
  }

  // Una nota es un Article: le da a los motores autor, fecha y editor, que es
  // lo que miran para decidir si citarla y a quien atribuirsela.
  const postSlug = postSlugFromPath(pathname);
  const post = postSlug ? getPost(postSlug) : undefined;

  if (post) {
    graph.push({
      "@type": "BlogPosting",
      "@id": `${url}#article`,
      headline: post.title,
      description: post.summary,
      datePublished: post.date,
      // Misma fecha que el sitemap (`updated` si la nota se revisó): una fecha
      // de modificación falsa le quita credibilidad a las notas que sí se tocaron.
      dateModified: post.updated || post.date,
      inLanguage: HTML_LANG[localeFromPath(pathname)],
      // Una nota firmada por una persona es de un Person, no de la
      // Organization: si no, el nombre del autor queda atribuido a la empresa.
      // Si firma el fundador se referencia su entidad, no se duplica.
      author: isTeamAuthor(post.author)
        ? { "@type": "Organization", name: post.author, "@id": orgId }
        : isSameAuthor(post.author, ORGANIZATION.founderName)
          ? { "@id": founderId }
          : {
              "@type": "Person",
              name: post.author,
              worksFor: { "@id": orgId },
              ...(authorProfileUrl(post.author)
                ? { url: authorProfileUrl(post.author), sameAs: [authorProfileUrl(post.author)] }
                : {}),
            },
      publisher: { "@id": orgId },
      isPartOf: { "@id": siteId },
      mainEntityOfPage: { "@id": `${url}#webpage` },
      image: OG_IMAGE,
      articleSection: post.tag,
      // wordCount y timeRequired le dan a los motores generativos una señal de
      // profundidad: distinguen una nota de fondo de un parrafo suelto a la
      // hora de decidir a quien citar.
      wordCount: post.wordCount,
      timeRequired: `PT${post.readingMinutes}M`,
      // El texto completo esta en la pagina: se lo decimos explicitamente para
      // que no asuman que hay contenido detras de un muro.
      isAccessibleForFree: true,
    });
  }

  // Breadcrumb: solo tiene sentido fuera de la home.
  if (seo.breadcrumb) {
    const locale = localeFromPath(pathname);

    // Se descarta el prefijo de idioma antes de mirar los segmentos: en
    // /en/cases/ecommerce el primero es "en", no "cases", y el nivel
    // intermedio no se encontraba nunca.
    const segments = pathname
      .split("/")
      .filter(Boolean)
      .filter((seg, i) => !(i === 0 && seg === "en"));

    const items: unknown[] = [
      {
        "@type": "ListItem",
        position: 1,
        name: locale === "en" ? "Home" : "Inicio",
        item: `${SITE_URL}${ROUTES.home[locale]}`,
      },
    ];

    // Nivel intermedio de las rutas anidadas. Se deriva del primer segmento:
    // estaba fijo en "Casos" y ponia "Inicio > Casos > <nota>" en las novedades.
    if (segments.length > 1) {
      // Google exige "item" en todo ListItem que no sea el ultimo de la
      // lista (Search Console lo reportaba como "Falta el campo item" en
      // todas las paginas de /caso/<slug> y /casos/<rubro>). "caso" -sin
      // pagina propia, es solo el prefijo de /caso/<slug>- apunta al mismo
      // indice que "casos": es la agrupacion real de la que cuelga.
      const parents: Record<string, { name: string; item: string }> = {
        casos: { name: "Casos", item: `${SITE_URL}${ROUTES.cases.es}` },
        caso: { name: "Casos", item: `${SITE_URL}${ROUTES.cases.es}` },
        novedades: { name: "Novedades", item: `${SITE_URL}/novedades` },
        cases: { name: "Cases", item: `${SITE_URL}${ROUTES.cases.en}` },
        case: { name: "Cases", item: `${SITE_URL}${ROUTES.cases.en}` },
        news: { name: "Insights", item: `${SITE_URL}/en/news` },
        // Las landings de solucion y de industria cuelgan de su indice, que es
        // el que enlaza la pagina visible ("← Todas las soluciones").
        soluciones: { name: "Soluciones", item: `${SITE_URL}${ROUTES.solutions.es}` },
        industrias: { name: "Industrias", item: `${SITE_URL}${ROUTES.industries.es}` },
        solutions: { name: "Solutions", item: `${SITE_URL}${ROUTES.solutions.en}` },
        industries: { name: "Industries", item: `${SITE_URL}${ROUTES.industries.en}` },
      };
      const parent = parents[segments[0]];

      if (parent) {
        items.push({
          "@type": "ListItem",
          position: 2,
          name: parent.name,
          item: parent.item,
        });
      }
    }

    items.push({
      "@type": "ListItem",
      position: items.length + 1,
      name: seo.breadcrumb,
      item: url,
    });

    graph.push({
      "@type": "BreadcrumbList",
      "@id": `${url}#breadcrumb`,
      itemListElement: items,
    });
  }

  return { "@context": "https://schema.org", "@graph": graph };
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * Genera el sitemap.xml a partir de las mismas rutas que se pre-renderizan,
 * asi no puede quedar desincronizado: una ruta nueva entra sola y una que se
 * elimina desaparece. Se excluye /404, que no debe indexarse.
 *
 * Vive aca y no en el script de prerender para que todo lo que declara URLs
 * publicas salga de este unico archivo.
 */
/** Valor del atributo lang del <html> para una ruta. Lo usa el prerender. */
export function htmlLangFor(pathname: string): string {
  return HTML_LANG[localeFromPath(pathname)];
}

/**
 * Fecha del ultimo cambio REAL de una ruta, o undefined si no se sabe.
 *
 * - Notas: `updated` o, si no hay, la fecha de publicacion.
 * - Listado de novedades: la nota mas reciente.
 * - Resto: la del grupo de contenido (src/data/lastmod.json, ver
 *   scripts/gen-lastmod.mjs).
 *
 * Antes todo lo que no era una nota declaraba la fecha del build, o sea "cambié
 * hoy" en cada deploy: los buscadores lo detectan y dejan de confiar en el
 * lastmod, que es la señal que sirve para revisitar solo lo que cambio. Sin
 * fecha confiable es mejor omitir la etiqueta que inventar una.
 */
function lastmodFor(route: string): string | undefined {
  const slug = postSlugFromPath(route);
  const post = slug ? getPost(slug) : undefined;
  if (post) return post.updated || post.date || undefined;

  const es = translatePath(route, "es");
  if (es === "/") return lastmodDates.home;
  if (es === "/nosotros") return lastmodDates.about;
  if (es === "/empresas") return lastmodDates.enterprise;
  if (es === "/novedades") {
    return posts.map((p) => p.updated || p.date).filter(Boolean).sort().at(-1);
  }
  if (es.startsWith("/caso")) return lastmodDates.cases;
  if (es.startsWith("/soluciones")) return lastmodDates.solutions;
  if (es.startsWith("/industrias")) return lastmodDates.industries;
  return undefined;
}

/**
 * Alternativas de idioma de una ruta para el sitemap (<xhtml:link>), las mismas
 * que ya declara el <head> de la pagina. Se omiten si la traduccion no es una
 * ruta pre-renderizada: un hreflang que apunta a un 404 es peor que ninguno.
 */
function sitemapAlternates(route: string, known: Set<string>): string[] {
  const locales = LOCALES.map((locale) => ({
    hreflang: HTML_LANG[locale],
    path: translateLocalizedPath(route, locale),
  }));
  if (!locales.every((l) => known.has(l.path))) return [];

  const link = (hreflang: string, path: string) =>
    `    <xhtml:link rel="alternate" hreflang="${hreflang}" href="${SITE_URL}${path}" />`;

  return [
    ...locales.map((l) => link(l.hreflang, l.path)),
    link("x-default", translateLocalizedPath(route, DEFAULT_LOCALE)),
  ];
}

export function renderSitemap(): string {
  const known = new Set(prerenderRoutes);
  const urls = prerenderRoutes.map((route) => {
    const post = postSlugFromPath(route) ? getPost(postSlugFromPath(route)!) : undefined;
    const lastmod = lastmodFor(route);

    // La home es la entrada principal; las notas envejecen y pesan menos que
    // las paginas de servicio.
    const priority = route === "/" || route === "/en" ? "1.0" : post ? "0.6" : "0.8";

    return [
      "  <url>",
      `    <loc>${SITE_URL}${route}</loc>`,
      ...(lastmod ? [`    <lastmod>${lastmod}</lastmod>`] : []),
      `    <changefreq>${post ? "yearly" : "monthly"}</changefreq>`,
      `    <priority>${priority}</priority>`,
      ...sitemapAlternates(route, known),
      "  </url>",
    ].join("\n");
  });

  return [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9" xmlns:xhtml="http://www.w3.org/1999/xhtml">',
    ...urls,
    "</urlset>",
    "",
  ].join("\n");
}

/**
 * Etiquetas <link rel="alternate" hreflang> de una ruta.
 *
 * Cada pagina declara donde vive su equivalente en el otro idioma. Sin esto,
 * Google puede leer /nosotros y /en/about como contenido duplicado y elegir
 * cual indexar por su cuenta.
 */
function renderAlternates(pathname: string): string[] {
  const tags = LOCALES.map((locale) => {
    const href = `${SITE_URL}${translateLocalizedPath(pathname, locale)}`;
    return `<link rel="alternate" hreflang="${HTML_LANG[locale]}" href="${href}" />`;
  });

  // x-default: a donde mandar a quien no calza con ningun idioma declarado.
  tags.push(
    `<link rel="alternate" hreflang="x-default" href="${SITE_URL}${translateLocalizedPath(pathname, DEFAULT_LOCALE)}" />`,
  );

  return tags;
}

// Serializa el JSON-LD para incrustarlo en un <script>. Escapar "<" es
// obligatorio: un "</script>" dentro de un string cerraria la etiqueta antes
// de tiempo y romperia la pagina (ademas de ser un vector de inyeccion).
export function serializeJsonLd(data: unknown): string {
  return JSON.stringify(data).replace(/</g, "\\u003c");
}

// Genera el bloque de etiquetas <head> (title + metas + datos estructurados)
// como string HTML, para inyectarlo en el HTML pre-renderizado de cada ruta.
export function renderHeadTags(pathname: string): string {
  const { title, description } = getSeo(pathname);
  const url = `${SITE_URL}${pathname}`;
  const t = escapeHtml(title);
  const d = escapeHtml(description);
  const postSlug = postSlugFromPath(pathname);
  const post = postSlug ? getPost(postSlug) : undefined;

  return [
    `<title>${t}</title>`,
    `<meta name="description" content="${d}" />`,
    // Herramientas internas (el panel) y propuestas comerciales: nunca
    // indexar ni seguir sus links, aunque alguien las enlace desde afuera
    // por error.
    ...(isNoindexPath(pathname)
      ? [`<meta name="robots" content="noindex, nofollow" />`]
      : []),
    `<link rel="canonical" href="${url}" />`,
    // hreflang: le dice a los buscadores que estas URLs son la misma pagina en
    // otro idioma, en vez de contenido duplicado. x-default apunta al español,
    // que es el idioma por defecto del sitio.
    ...renderAlternates(pathname),
    `<meta property="og:type" content="${post ? "article" : "website"}" />`,
    ...(post
      ? [
          `<meta property="article:published_time" content="${escapeHtml(post.date)}" />`,
          `<meta property="article:modified_time" content="${escapeHtml(post.updated || post.date)}" />`,
        ]
      : []),
    `<meta property="og:site_name" content="${SITE_NAME}" />`,
    `<meta property="og:locale" content="${OG_LOCALE[localeFromPath(pathname)]}" />`,
    `<meta property="og:title" content="${t}" />`,
    `<meta property="og:description" content="${d}" />`,
    `<meta property="og:url" content="${url}" />`,
    `<meta property="og:image" content="${OG_IMAGE}" />`,
    `<meta property="og:image:width" content="${OG_IMAGE_WIDTH}" />`,
    `<meta property="og:image:height" content="${OG_IMAGE_HEIGHT}" />`,
    `<meta property="og:image:alt" content="${escapeHtml(SITE_NAME)}" />`,
    `<meta name="twitter:card" content="summary_large_image" />`,
    `<meta name="twitter:title" content="${t}" />`,
    `<meta name="twitter:description" content="${d}" />`,
    `<meta name="twitter:image" content="${OG_IMAGE}" />`,
    // El id tiene que coincidir con el que busca upsertJsonLd en App.tsx: al
    // hidratar, el cliente reutiliza este script en vez de agregar un segundo.
    `<script type="application/ld+json" id="${JSONLD_SCRIPT_ID}">${serializeJsonLd(buildJsonLd(pathname))}</script>`,
  ].join("\n        ");
}
